import React, {
  ReactElement,
  useEffect,
  useState,
  useCallback,
} from 'react';
import {
  Autocomplete,
  Button as MUIButton,
  IconButton,
  Paper,
  Popper,
  Skeleton,
  Stack,
  Table,
  TableContainer,
  TextField,
  Typography,
} from '@mui/material';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import api from '../../services/api';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import { serviceProvider } from '../../schema/serviceProvider';
import { enumItems } from '../../schema/enumItems';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import { debounce } from 'lodash';
import { SlidingPaneEdit } from '../Page/SlidingPaneEdit';
import { service } from '../../schema/service';
import { SlidingPaneAddNew } from '../Page/SlidingPaneAddNew';
import DialogContent from '@mui/material/DialogContent';
import TableBody from '@mui/material/TableBody';
import { serviceProviderStripperRole } from '../../schema/serviceProviderStripperRole';
import CloudDoneRoundedIcon from '@mui/icons-material/CloudDoneRounded';
import Fade from '@mui/material/Fade';
import CircularProgress from '@mui/material/CircularProgress';
import { convertFromRaw } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import ServiceProviderServiceRow from './ServiceProviderServiceRow';
import { formatNumber } from '../../reducer/actions/formatNumber';
import toast from 'react-hot-toast';

const parseJsonToEditorState = (jsonData: any) => {
  const contentState = convertFromRaw(JSON.parse(jsonData)); // Visszaalakítjuk a JSON-t ContentState-re
  const html = stateToHTML(contentState); // A ContentState HTML-re alakítása
  return html;
};

const isJsonString = (str: string): boolean => {
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
};

export const ServiceProviderServices = ({
  schema,
  values,
  tableType,
  forcedValues,
  onChange,
}: {
  schema: any;
  values: any;
  forcedValues: any;
  tableType: string;
  onChange: (column: string, value: any) => void;
}): ReactElement => {
  const [openedSlidingPane, setOpenedSlidingPane] = useState<any>({});

  const [currentAddNewPaneEntity, setCurrentAddNewPaneEntity] = useState<any>({
    schema: schema,
    row: {},
  });

  const [openConfirmDialog, setOpenConfirmDialog] = useState<any>(false);
  const [openViewPrices, setOpenViewPrices] = useState<any>(undefined);
  const [openViewPriceColumns, setOpenViewPriceColumns] = useState<any>({});
  const [properties, setProperties] = useState<any>(undefined);
  const [stripperRoleOptions, setStripperRoleOptions] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [type, setType] = useState<string>('default');
  const debouncedOnChange = debounce(handleDebouncedOnChange, 0);
  const [currentState, setCurrentState] = useState<any>({
    value: values,
    send: false,
  });

  const [query, setQuery] = React.useState<'idle' | 'progress' | 'success'>(
    'idle'
  );
  const timerRef = React.useRef<ReturnType<typeof setTimeout>>();

  function handleDebouncedOnChange(
    value: any,
    schema: any,
    tab: string,
    id: number
  ) {
    const data: any = {};
    data[`${tab}`] = value;

    const currentProperties = { ...properties };
  }

  const handleDeleteService = useCallback(
    async (deletedRow: any) => {
      api
        .delete(`/services/${deletedRow?.id}`)
        .then((res: any) => {
          toast.success(
            `Successfully deleted Service: ${
              deletedRow?.name ? deletedRow?.name : deletedRow?.id
            }`
          );
          setOpenConfirmDialog(false);
        })
        .catch((e: any) => {
          console.error(e);
          toast.error(
            `Error: Unable to delete Service: ${
              deletedRow?.name ? deletedRow?.name : deletedRow?.id
            }`
          );
        });
    },
    [properties]
  );

  const CustomPopper = function (props: any) {
    return (
      <>
        <div>
          <Popper
            {...props}
            sx={{
              '& .MuiPaper-root': {
                boxShadow: '0 4px 10px -1px rgb(17 20 24 / 20%)',
              },
            }}
            style={{
              width: 'fit-content',
              maxWidth: '300px',
            }}
            placement="bottom-start"
          />
        </div>
      </>
    );
  };

  const fetchOptions = useCallback(() => {
    const searchParams = new URLSearchParams({});

    return enumItems.stripperRole.client
      .readAll(1, searchParams)
      .then((res: any) => {
        /*let newObj: any = { name: '(BLANK)' };
      res.data.unshift(newObj);*/

        setStripperRoleOptions(res.data);
      });
  }, []);

  const fetchServices = useCallback(() => {
    setLoading(true);
    const searchParams = new URLSearchParams({});

    const prices = api
      .get(`/service-provider-services/${values?.id}`, searchParams)
      .then((res: any) => {
        setProperties(res.data);
        setType(res.type);
        setCurrentState({
          value: res.data,
          send: false,
          key: undefined,
        });
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchServices();

    const columns: any = {};
    for (let i = 1; i < 51; i++) {
      columns[`size_${i}`] = i;
    }

    setOpenViewPriceColumns(columns);
  }, []);

  const debouncedDragOnChange = debounce(handleDebouncedOnDragChange, 200);

  const moveRow = (fromIndex: any, toIndex: any) => {
    debouncedDragOnChange(fromIndex, toIndex);
  };

  function handleDebouncedOnDragChange(fromIndex: any, toIndex: any) {
    const updatedRows = [...properties];
    const [movedRow] = updatedRows.splice(fromIndex, 1);
    updatedRows.splice(toIndex, 0, movedRow);
    setProperties(updatedRows);

    updatedRows.map((item, index) => {
      api.patch(`/services/${item?.id}`, {
        position: index,
      });
    });
  }

  return (
    <>
      {loading ? (
        <>
          <Skeleton variant="rectangular" width={'100%'} height={30} />
          <Skeleton
            variant="rectangular"
            width={'100%'}
            height={100}
            sx={{
              marginTop: '10px',
            }}
          />
        </>
      ) : (
        <>
          {type === 'default' ? (
            <>
              <TableContainer component={Paper}>
                <Table aria-label="simple table" size="small">
                  <TableHead
                    sx={{
                      '& .MuiTableCell-root': {
                        color: 'inherit',
                        border: 'none',
                      },
                      '& .MuiTableRow-root .MuiTableCell-root.MuiTableCell-head': {
                        border: 'none',
                        fontSize: '14px',
                        p: '10px',
                      },
                      background: '#256861',
                      color: '#fff',
                    }}
                  >
                    <TableRow
                      sx={{
                        '& .MuiTableCell-root': {
                          overflowWrap: 'anywhere',
                        },
                      }}
                    >
                      <TableCell></TableCell>
                      <TableCell>Service Name</TableCell>
                      <TableCell>Updated</TableCell>
                      <TableCell>Valid From</TableCell>
                      <TableCell>Valid To</TableCell>
                      <TableCell>Cost Type</TableCell>
                      <TableCell>Staff Required</TableCell>
                      <TableCell>Includes</TableCell>
                      <TableCell align={'right'}>Cost</TableCell>
                      <TableCell align={'right'}>Cost Gross</TableCell>
                      <TableCell align={'left'}>CCY</TableCell>

                      <TableCell align={'right'}>VAT</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {properties ? (
                      properties?.map((row: any, index: number) => {
                        const type = row?.service_type
                          ? row.service_type_id === 1 ||
                            row.service_type_id === 2
                            ? 'unit'
                            : 'var'
                          : 'unit';

                        const htmlIncludes =
                          row?.includes_notes &&
                          isJsonString(row.includes_notes)
                            ? parseJsonToEditorState(row.includes_notes)
                            : row?.includes_notes
                            ? row.includes_notes
                            : '';

                        return (
                          <ServiceProviderServiceRow
                            row={row}
                            index={index}
                            type={type}
                            notes={htmlIncludes}
                            moveRow={(fromIndex: any, toIndex: any) => {
                              moveRow(fromIndex, toIndex);
                            }}
                            clickOnEdit={() => {
                              setOpenedSlidingPane({
                                schema: service,
                                row: row,
                                tab: 'services',
                              });
                            }}
                            clickOnDelete={() => {
                              setOpenConfirmDialog({
                                row: row,
                                schema: service,
                              });
                            }}
                            openViewPrices={() => {
                              setOpenViewPrices(row);
                            }}
                          />
                        );
                      })
                    ) : (
                      <></>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Stack
                direction={'row'}
                alignItems={'center'}
                sx={{
                  width: '100%',
                }}
                justifyContent={'space-between'}
              >
                <Stack direction={'row'}>
                  <Tooltip
                    title={`Add new Service`}
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, -14],
                            },
                          },
                        ],
                      },
                    }}
                  >
                    <IconButton
                      color={'primary'}
                      onClick={() => {
                        console.log('ADD NEW ROW');
                        setCurrentAddNewPaneEntity({
                          row: {
                            id: 'new',
                            service_provider: {
                              id: values?.id,
                              name: values?.name,
                            },
                          },
                          schema: service,
                        });
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
                <Stack>
                  {query === 'success' ? (
                    <Stack direction={'row'} alignItems={'center'}>
                      <CloudDoneRoundedIcon color={'disabled'} />
                      <Typography
                        variant={'caption'}
                        marginLeft={'5px'}
                        color={'gray'}
                      >
                        successfully saved
                      </Typography>
                    </Stack>
                  ) : (
                    <Fade
                      in={query === 'progress'}
                      style={{
                        transitionDelay: query === 'progress' ? '800ms' : '0ms',
                      }}
                      unmountOnExit
                    >
                      <CircularProgress size={20} />
                    </Fade>
                  )}
                </Stack>
              </Stack>
            </>
          ) : (
            <>
              <Stack
                sx={{
                  marginTop: '20px',
                  width: '100%',
                }}
                gap={2}
                direction={'row'}
              >
                <Stack
                  direction={'column'}
                  alignItems={'flex-start'}
                  sx={{
                    width: '50%',
                  }}
                >
                  <Stack width={'100%'}>
                    <span>Stripper roles</span>

                    {loading ? (
                      <>
                        <Skeleton
                          variant="rectangular"
                          width={'100%'}
                          height={30}
                        />
                        <Skeleton
                          variant="rectangular"
                          width={'100%'}
                          height={30}
                          sx={{
                            marginTop: '5px',
                          }}
                        />
                        <Skeleton
                          variant="rectangular"
                          width={'100%'}
                          height={30}
                          sx={{
                            marginTop: '5px',
                          }}
                        />
                      </>
                    ) : (
                      <>
                        {properties.map(
                          (stripperRoleItem: any, index: number) => {
                            return (
                              <Stack
                                key={index}
                                alignItems={'center'}
                                direction={'row'}
                                width={'100%'}
                              >
                                <Autocomplete
                                  sx={{
                                    '& .MuiInputBase-root.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline, .MuiDrawer-docked .MuiInputBase-root.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                                      borderColor: 'rgba(0,0,0,0.2) !important',
                                    },
                                    '& .MuiButtonBase-root.MuiIconButton-root.MuiAutocomplete-clearIndicator': {
                                      display: 'none',
                                    },
                                  }}
                                  disabled
                                  size={'small'}
                                  fullWidth={true}
                                  PopperComponent={CustomPopper}
                                  onOpen={() => {
                                    fetchOptions();
                                  }}
                                  value={stripperRoleItem}
                                  getOptionLabel={(option: any) => option?.name}
                                  options={stripperRoleOptions}
                                  renderInput={(params) => (
                                    <TextField {...params} label="" />
                                  )}
                                  onChange={(event, newValue) => {
                                    debouncedOnChange(
                                      newValue,
                                      serviceProviderStripperRole,
                                      'stripper_role',
                                      stripperRoleItem?.id
                                    );
                                  }}
                                />

                                <IconButton
                                  color={'error'}
                                  size={'small'}
                                  onClick={() => {
                                    setOpenConfirmDialog({
                                      row: stripperRoleItem,
                                      schema: serviceProviderStripperRole,
                                    });
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>

                                {/*  <MoreOptions
                                clickOnEdit={() => {
                                  setOpenedSlidingPane({
                                    schema: enumItems,
                                    row: stripperRoleItem,
                                  });
                                }}
                                clickOnDelete={() => {

                                }}
                              />*/}
                              </Stack>
                            );
                          }
                        )}
                      </>
                    )}
                  </Stack>
                  <Tooltip
                    title={`Add new`}
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, -14],
                            },
                          },
                        ],
                      },
                    }}
                  >
                    <IconButton
                      color={'primary'}
                      disabled={loading}
                      onClick={() => {
                        setCurrentAddNewPaneEntity({
                          row: {
                            id: 'new',
                            service_provider: {
                              id: values?.id,
                              name: values?.name,
                            },
                          },
                          schema: serviceProviderStripperRole,
                        });
                      }}
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>
            </>
          )}
        </>
      )}

      {openViewPrices?.name && (
        <Dialog
          open={openViewPrices}
          onClose={() => {
            setOpenViewPrices(undefined);
          }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          sx={{
            '& .MuiPaper-root.MuiDialog-paper': {
              width: '100%',
              maxWidth: '100%',
            },
          }}
        >
          <DialogTitle id="alert-dialog-title">
            {`${openViewPrices?.name} - Prices`}{' '}
            {openViewPrices?.currency?.name &&
              `(${openViewPrices?.currency?.name})`}
          </DialogTitle>

          <DialogContent>
            <TableContainer component={Paper}>
              <Table aria-label="simple table" size="small" sx={{}}>
                <TableHead
                  sx={{
                    '& .MuiTableCell-root': {
                      color: 'inherit',
                      padding: '5px',
                    },
                    '& .MuiTableRow-root .MuiTableCell-root.MuiTableCell-head': {
                      border: 'none',
                    },
                    background: '#256861',
                    color: '#fff',
                  }}
                >
                  <TableRow
                    sx={{
                      '& .MuiTableCell-root': {
                        overflowWrap: 'anywhere',
                      },
                    }}
                  >
                    {Object.keys(openViewPriceColumns).map((column: any) => {
                      return (
                        <TableCell
                          sx={{
                            textAlign: 'center',
                          }}
                        >
                          {openViewPriceColumns[column]}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow sx={{}}>
                    {Object.keys(openViewPriceColumns).map((column: any) => {
                      return (
                        <TableCell
                          sx={{
                            borderRight: '1px solid #e3e3e3',
                            padding: '5px 10px',
                          }}
                        >
                          {openViewPrices[`${column}`]
                            ? formatNumber(openViewPrices[`${column}`])
                            : '-'}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </DialogContent>
        </Dialog>
      )}

      {openConfirmDialog && (
        <Dialog
          open={openConfirmDialog}
          onClose={() => {
            setOpenConfirmDialog(false);
          }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {'Are you sure you want to delete it?'}
          </DialogTitle>
          <DialogActions>
            <MUIButton
              onClick={() => {
                setOpenConfirmDialog(false);
              }}
            >
              Cancel
            </MUIButton>
            <MUIButton
              variant={'contained'}
              color={'error'}
              onClick={() => {
                const deletedRow = openConfirmDialog?.row;
                openConfirmDialog?.schema.client
                  .delete(deletedRow.id as number, {
                    id: deletedRow.id,
                    service_provider: {
                      id: values.id,
                    },
                  })
                  .then((res: any) => {
                    fetchServices();
                    toast.success(
                      `Successfully deleted ${
                        openConfirmDialog?.schema?.name
                      }: ${
                        deletedRow?.name ? deletedRow?.name : deletedRow?.id
                      }`
                    );
                    setOpenConfirmDialog(false);

                    setQuery('progress');
                    timerRef.current = setTimeout(() => {
                      setQuery('success');
                    }, 2000);
                  })
                  .catch((e: any) => {
                    toast.error(
                      `Error: Unable to delete ${
                        openConfirmDialog?.schema?.name
                      }: ${
                        deletedRow?.name ? deletedRow?.name : deletedRow?.id
                      }`
                    );
                  });
              }}
              autoFocus
            >
              Delete
            </MUIButton>
          </DialogActions>
        </Dialog>
      )}

      {openedSlidingPane?.row?.id && (
        <SlidingPaneEdit
          schema={openedSlidingPane?.schema}
          id={openedSlidingPane?.row?.id}
          onSubmit={() => {
            fetchServices();
            setOpenedSlidingPane({});
            setQuery('progress');
            timerRef.current = setTimeout(() => {
              setQuery('success');
            }, 2000);
          }}
          propertiesFromTable={
            openedSlidingPane?.row ? openedSlidingPane?.row : {}
          }
          onClose={(value) => {
            setOpenedSlidingPane({});
          }}
        />
      )}

      {currentAddNewPaneEntity?.row?.id && (
        <SlidingPaneAddNew
          schema={currentAddNewPaneEntity?.schema}
          mainSchema={serviceProvider}
          id={currentAddNewPaneEntity?.row?.id}
          hasSlidingPane={false}
          onSubmit={(result: any) => {
            fetchServices();
            setCurrentAddNewPaneEntity({
              schema: schema,
              row: {},
              key: undefined,
            });

            setQuery('progress');
            timerRef.current = setTimeout(() => {
              setQuery('success');
            }, 2000);
          }}
          propertiesFromTable={
            currentAddNewPaneEntity?.row ? currentAddNewPaneEntity?.row : {}
          }
          row={currentAddNewPaneEntity?.row ? currentAddNewPaneEntity?.row : {}}
          onClose={(value) => {
            setCurrentAddNewPaneEntity({
              schema: schema,
              row: {},
              key: undefined,
            });
          }}
        />
      )}
    </>
  );
};
